/**
 * E-learning Activation Page
 *
 * Displayed when an invitee clicks the activation link in their email.
 * Uses Checkout Success card pattern (centered card with icon).
 *
 * Requires authentication. If not logged in, redirects to the login page
 * with a destination param so the user returns here after login.
 *
 * Flow:
 * 1. Check auth → redirect to login if not logged in
 * 2. Page loads → calls backend activation API
 * 3. On success → shows success message + buttons to course and products
 * 4. On error → shows appropriate error message
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory, useLocation } from 'react-router';

import { Button } from '../../../components';
import { DEFAULT_LANGUAGE } from '../../../config/constants';
import {
  ROUTE_ACCOUNT_LOGIN,
  ROUTE_ACCOUNT_REGISTER,
  ROUTE_ACCOUNT_PRODUCTS,
} from '../../../config/routes';
import useLoggedInStatus from '../../../hooks/useLoggedInstatus';
import useCurrentUser from '../../../hooks/useCurrentUser';
import {
  activateElearningInvitation,
  type ActivationResponse,
} from '../../../store/actions/Orders';

import './style.scss';

interface ActivateParams {
  invitationId: string;
  token: string;
}

type ActivationState = 'loading' | 'success' | 'error';

interface ActivationResult {
  state: ActivationState;
  courseUrl?: string;
  errorCode?: string;
}

const ActivateElearning: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { invitationId, token } = useParams<ActivateParams>();
  const { isLoggedIn } = useLoggedInStatus();
  const user = useCurrentUser();
  const [result, setResult] = useState<ActivationResult>({ state: 'loading' });

  // Wait for the user profile to be loaded before activating.
  // After registration, the profile save is async — activating too early
  // means Moodle gets empty names instead of the actual user name.
  const profileReady = Boolean(
    user?.profile?.field_first_name?.[0]?.value,
  );

  // Extract invitee email and account status from the activation URL query params.
  const urlParams = new URLSearchParams(location.search);
  const inviteeEmail = urlParams.get('email') ?? '';
  const hasAccount = urlParams.get('has_account') === '1';

  // Redirect to login (existing user) or register (new user) if not authenticated.
  // Pass the invitee email so the form can pre-fill it.
  useEffect(() => {
    if (isLoggedIn === false) {
      const currentPath = `/elearning/activate/${invitationId}/${token}`;
      const emailParam = inviteeEmail ? `&email=${encodeURIComponent(inviteeEmail)}` : '';
      const authRoute = hasAccount ? ROUTE_ACCOUNT_LOGIN : ROUTE_ACCOUNT_REGISTER;
      history.push(
        `${authRoute}?destination=${encodeURIComponent(currentPath)}${emailParam}`,
      );
    }
  }, [isLoggedIn, invitationId, token, inviteeEmail, hasAccount, history]);

  // Activate invitation once logged in and profile is available.
  useEffect(() => {
    if (isLoggedIn !== true || !profileReady) {
      return;
    }

    const activate = async () => {
      try {
        const response: ActivationResponse = await activateElearningInvitation(
          parseInt(invitationId, 10),
          token,
        );

        if (response.success && response.course_url) {
          setResult({ state: 'success', courseUrl: response.course_url });
        } else {
          setResult({
            state: 'error',
            errorCode: response.error_code || 'UNKNOWN_ERROR',
          });
        }
      } catch {
        setResult({ state: 'error', errorCode: 'ACTIVATION_FAILED' });
      }
    };

    activate().catch(() => {
      setResult({ state: 'error', errorCode: 'ACTIVATION_FAILED' });
    });
  }, [isLoggedIn, profileReady, invitationId, token]);

  const getErrorMessage = (errorCode?: string): string => {
    switch (errorCode) {
      case 'INVITATION_NOT_FOUND':
        return t('elearning_activate.error_not_found');
      case 'INVALID_TOKEN':
        return t('elearning_activate.error_invalid_token');
      case 'LICENSE_EXPIRED':
        return t('elearning_activate.error_license_expired');
      case 'EMAIL_MISMATCH':
        return t('elearning_activate.error_email_mismatch');
      default:
        return t('elearning_activate.error_generic');
    }
  };

  // Show nothing while checking auth status.
  if (isLoggedIn === null || isLoggedIn === false) {
    return null;
  }

  return (
    <>
      <Helmet htmlAttributes={{ lang: DEFAULT_LANGUAGE }}>
        <title>{t('elearning_activate.page_title')}</title>
      </Helmet>
      <div className="PageActivateElearning" id="main">
        <div className="PageActivateElearning__container">
          {result.state === 'loading' && (
            <>
              <div className="StepsProgressBar__step__number__done">
                <span><div className="icon icon-check icon-variant-2" /></span>
              </div>
              <h2>{t('elearning_activate.title')}</h2>
              <p>{t('elearning_activate.activating')}</p>
            </>
          )}
          {result.state === 'success' && (
            <>
              <div className="StepsProgressBar__step__number__done">
                <span><div className="icon icon-check icon-variant-2" /></span>
              </div>
              <h2>{t('elearning_activate.title')}</h2>
              <p>{t('elearning_activate.success')}</p>
              <div className="PageActivateElearning__actions">
                <Button
                  type="primary"
                  onClick={() => {
                    if (result.courseUrl) {
                      window.location.href = result.courseUrl;
                    }
                  }}
                  className="Button Button--primary"
                >
                  {t('elearning_activate.go_to_course')}
                </Button>
                <Button
                  type="secondary"
                  onClick={() => history.push(ROUTE_ACCOUNT_PRODUCTS)}
                  className="Button Button--secondary"
                >
                  {t('elearning_activate.go_to_products')}
                </Button>
              </div>
            </>
          )}
          {result.state === 'error' && (
            <>
              <div className="StepsProgressBar__step__number__done">
                <span><div className="icon icon-cross icon-variant-2" /></span>
              </div>
              <h2>{t('elearning_activate.title')}</h2>
              <p>{getErrorMessage(result.errorCode)}</p>
              {result.errorCode === 'LICENSE_EXPIRED' && (
                <p className="PageActivateElearning__support">
                  {t('elearning_activate.contact_support')}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ActivateElearning;
